// The public landing at '/'. What it shows depends on the session:
//   - signed out (GET /api/me => 401, or any error): a CUSTOMER-first landing.
//     It tells a visitor with a booking link to open it, and offers a "message
//     Numaco" contact form for visitors without one (POST /api/contact emails
//     info@numaco.ch). It never lists hosts (a public host directory is out of
//     scope per ADR-0002 sections 5, 8); the form routes the enquiry to one
//     inbox. A quiet "Numaco employee? Sign in" link routes employees to /admin.
//   - signed in (a provisioned host): a concise "manage your page" panel with
//     the host's shareable booking link and a button into the admin settings.
//   - checking: a small loading block, so neither panel flashes wrongly.
// Implements: ADR-0002 section 5.

import { useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { ApiError } from '../api/types.js';
import type { SignedInHost } from '../api/types.js';
import { api } from '../api/client.js';
import { BrandShell } from '../components/BrandShell.js';
import { Banner, Spinner } from '../components/feedback.js';
import { validateContact, type ContactFieldErrors } from '../lib/validation.js';
import { bookingAbsoluteLink, bookingPathLink, navigate } from '../lib/router.js';

type AuthState = 'checking' | 'signed-out' | 'signed-in';

export function LandingPage(): JSX.Element {
  const [auth, setAuth] = useState<AuthState>('checking');
  const [host, setHost] = useState<SignedInHost | null>(null);

  // Probe the session via GET /api/me. Any thrown error (401 when there is no
  // session, but also a network failure) means "treat as new visitor" and show
  // the onboarding. An ignore guard stops a late resolve from setting state on
  // an unmounted component.
  useEffect(() => {
    let ignore = false;
    void (async () => {
      try {
        const res = await api.getMe();
        if (ignore) return;
        setHost(res.host);
        setAuth('signed-in');
      } catch {
        if (ignore) return;
        setHost(null);
        setAuth('signed-out');
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  if (auth === 'checking') {
    // Neutral, customer-first copy: this paints before we know whether the visitor
    // is a host or a customer, so it must not show host-onboarding hero text.
    return (
      <BrandShell
        lede="Book a meeting with Numaco."
        sub="Pick a time with the person you are meeting, or send us a message and we will route you."
      >
        <div className="state">
          <Spinner />
          <p>Loading…</p>
        </div>
      </BrandShell>
    );
  }

  if (auth === 'signed-in' && host) {
    return <ManagePanel host={host} />;
  }

  return <SignedOutLanding />;
}

// ---------------- Signed-in: manage your page ----------------

function ManagePanel({ host }: { host: SignedInHost }): JSX.Element {
  return (
    <BrandShell
      lede="Your booking page is live."
      sub="Share your link, or fine-tune your settings."
    >
      <header className="page-head">
        <span className="eyebrow">Numaco Meet</span>
        <h1 className="page-title">Your booking page is live</h1>
        <p className="page-desc">
          Welcome back, {host.displayName}. Share your link to let people book a time, or open your
          settings to fine-tune booking types, hours, and connected calendars.
        </p>
      </header>

      <BookingLinkCard slug={host.slug} />

      <div className="landing-cta">
        <button type="button" className="btn btn-primary" onClick={() => navigate('/admin')}>
          Manage your page
        </button>
      </div>
    </BrandShell>
  );
}

/** The host's shareable booking link, with a copy control. Mirrors AdminPage. */
function BookingLinkCard({ slug }: { slug: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const absolute = bookingAbsoluteLink(slug);
  const path = bookingPathLink(slug);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }

  return (
    <section className="admin-section booking-link-card">
      <h2>Your booking link</h2>
      <p className="sec-desc">Send this to anyone. They pick a time and you get an invite.</p>
      <div className="booking-link-row">
        <a className="booking-link" href={path}>
          {absolute}
        </a>
        <button type="button" className="btn btn-ghost" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </section>
  );
}

// ---------------- Signed-out: customer-first landing ----------------

// Shown to visitors without a session. Customer-first: open your booking link if
// you have one, or message Numaco to be routed. A quiet employee sign-in link
// sits at the foot.
function SignedOutLanding(): JSX.Element {
  return (
    <BrandShell
      lede="Book a meeting with Numaco."
      sub="Pick a time with the person you are meeting, or send us a message and we will route you."
    >
      <header className="page-head">
        <span className="eyebrow">Numaco Meet</span>
        <h1 className="page-title">Book a meeting with Numaco</h1>
        <p className="page-desc">
          Pick a time with the person you are meeting, or send us a message and we will route you to
          the right person.
        </p>
      </header>

      <section className="landing-points" aria-label="How to book">
        <div className="landing-block">
          <h2>Have a booking link?</h2>
          <p>
            Open the link your Numaco contact sent you to choose a time. Every booking is with a
            specific person.
          </p>
        </div>
        <div className="landing-block">
          <h2>No link yet?</h2>
          <p>Tell us what you need and we will connect you with the right person.</p>
        </div>
      </section>

      <ContactForm />

      <p className="landing-foot">
        <button type="button" className="btn-link" onClick={() => navigate('/admin')}>
          Numaco employee? Sign in
        </button>
      </p>
    </BrandShell>
  );
}

type ContactStatus = 'idle' | 'submitting' | 'success';

// The "message Numaco" form. Posts to POST /api/contact, which emails the enquiry
// to info@numaco.ch. Includes a visually hidden honeypot field (`website`) that a
// human never fills; the server silently drops a filled value. On success the
// form is replaced by a confirmation (focus moves to it); on error a banner is
// shown (focus moves to it) with a rate-limit-specific message for 429.
function ContactForm(): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // The honeypot. A real visitor leaves this empty; bots tend to fill every field.
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<ContactFieldErrors>({});
  const [status, setStatus] = useState<ContactStatus>('idle');
  const [submitError, setSubmitError] = useState<string>();

  const successRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Move focus to the success confirmation so a screen-reader user is taken to the
  // outcome rather than left on the now-removed form.
  useEffect(() => {
    if (status === 'success') successRef.current?.focus();
  }, [status]);

  // Move focus to the error banner when a submit fails, for the same reason.
  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError]);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(undefined);
    const result = validateContact({ name, email, message });
    if (!result.ok) {
      setErrors(result.errors);
      // Move focus to the FIRST invalid control (in source order) so a keyboard /
      // screen-reader user lands on the field to fix. The field-error spans carry
      // role="alert" so the messages are announced as they appear. Mirrors the
      // success/submit-error focus handling above.
      if (result.errors.name) nameRef.current?.focus();
      else if (result.errors.email) emailRef.current?.focus();
      else if (result.errors.message) messageRef.current?.focus();
      return;
    }
    setErrors({});
    setStatus('submitting');
    try {
      await api.sendContact({ ...result.data, website });
      setStatus('success');
    } catch (err) {
      setStatus('idle');
      if (err instanceof ApiError && err.status === 429) {
        setSubmitError(
          'You have sent a few messages already, please try again in a little while.',
        );
      } else {
        setSubmitError('Sorry, we could not send your message. Please try again in a moment.');
      }
    }
  }

  if (status === 'success') {
    return (
      <div className="state" role="status" tabIndex={-1} ref={successRef}>
        <h3>Thanks, we will be in touch shortly.</h3>
        <p>We have received your message and will route it to the right person.</p>
      </div>
    );
  }

  const submitting = status === 'submitting';

  return (
    <form
      className="form rise landing-form"
      onSubmit={(e) => void handleSubmit(e)}
      noValidate
      aria-label="Contact Numaco"
    >
      <h2 className="page-title" style={{ fontSize: 'var(--fs-h3)' }}>
        Send us a message
      </h2>

      {submitError ? (
        <div ref={errorRef} tabIndex={-1}>
          <Banner kind="error">{submitError}</Banner>
        </div>
      ) : null}

      <div className={`field ${errors.name ? 'has-error' : ''}`}>
        <label htmlFor="c-name">
          Name <span className="req">*</span>
        </label>
        <input
          id="c-name"
          name="name"
          type="text"
          autoComplete="name"
          ref={nameRef}
          aria-required="true"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={errors.name ? 'err-c-name' : undefined}
        />
        {errors.name ? (
          <span className="field-error" id="err-c-name" role="alert">
            {errors.name}
          </span>
        ) : null}
      </div>

      <div className={`field ${errors.email ? 'has-error' : ''}`}>
        <label htmlFor="c-email">
          Email <span className="req">*</span>
        </label>
        <input
          id="c-email"
          name="email"
          type="email"
          autoComplete="email"
          ref={emailRef}
          aria-required="true"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? 'err-c-email' : 'hint-c-email'}
        />
        {errors.email ? (
          <span className="field-error" id="err-c-email" role="alert">
            {errors.email}
          </span>
        ) : (
          <span className="field-hint" id="hint-c-email">
            We will reply to this address.
          </span>
        )}
      </div>

      <div className={`field ${errors.message ? 'has-error' : ''}`}>
        <label htmlFor="c-message">
          Message <span className="req">*</span>
        </label>
        <textarea
          id="c-message"
          name="message"
          ref={messageRef}
          aria-required="true"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us what you need and who you are hoping to meet."
          aria-invalid={errors.message ? true : undefined}
          aria-describedby={errors.message ? 'err-c-message' : undefined}
        />
        {errors.message ? (
          <span className="field-error" id="err-c-message" role="alert">
            {errors.message}
          </span>
        ) : null}
      </div>

      {/* Honeypot: visually hidden, removed from the tab order and the a11y tree.
          A human never sees or fills it; a bot that does is silently dropped. */}
      <div className="sr-only" aria-hidden="true">
        <label htmlFor="c-website">Leave this field empty</label>
        <input
          id="c-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
        {submitting ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}
